/**
 * SEO Service - Dynamic meta tag management for SPA
 * Supports: pt-BR, en-US, es-ES
 */

const BASE_URL = 'https://summdash.com';

class SEOService {
  constructor() {
    // All supported locales for international SEO
    this.supportedLocales = ['pt-BR', 'en-US', 'es-ES', 'pt-PT', 'en-GB'];

    this.defaultMeta = {
      'pt-BR': {
        title: 'SummDash - Gestão Inteligente de Campanhas Meta Ads',
        description: 'Centralize a gestão de campanhas Meta Ads e Google Ads. Relatórios profissionais, automação e análise de performance para agências de marketing digital.',
        keywords: 'meta ads, google ads, gestão de campanhas, relatórios, agência de marketing, dashboard, automação'
      },
      'en-US': {
        title: 'SummDash - Smart Meta Ads Campaign Management',
        description: 'Centralize Meta Ads and Google Ads management. Professional reports, automation and performance analytics for digital marketing agencies.',
        keywords: 'meta ads, google ads, campaign management, reports, marketing agency, dashboard, automation'
      },
      'es-ES': {
        title: 'SummDash - Gestión Inteligente de Campañas Meta Ads',
        description: 'Centralice la gestión de campañas Meta Ads y Google Ads. Informes profesionales, automatización y análisis de rendimiento para agencias de marketing digital.',
        keywords: 'meta ads, google ads, gestión de campañas, informes, agencia de marketing, dashboard, automatización'
      },
      'pt-PT': {
        title: 'SummDash - Gestão Inteligente de Campanhas Meta Ads',
        description: 'Centralize a gestão de campanhas Meta Ads e Google Ads. Relatórios profissionais, automação e análise de desempenho para agências de marketing digital.',
        keywords: 'meta ads, google ads, gestão de campanhas, relatórios, agência de marketing, dashboard, automação'
      },
      'en-GB': {
        title: 'SummDash - Smart Meta Ads Campaign Management',
        description: 'Centralise Meta Ads and Google Ads management. Professional reports, automation and performance analytics for digital marketing agencies.',
        keywords: 'meta ads, google ads, campaign management, reports, marketing agency, dashboard, automation'
      }
    };

    this.organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'SummDash',
      url: BASE_URL,
      logo: `${BASE_URL}/images/logo_black.png`,
      description: 'Platform for digital marketing agencies to manage Meta Ads and Google Ads campaigns',
      foundingDate: '2024',
      sameAs: [
        'https://www.linkedin.com/company/summdash',
        'https://twitter.com/summdash'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'support@summdash.com',
        availableLanguage: ['Portuguese', 'English', 'Spanish']
      }
    };

    // Multi-currency pricing for different regions
    this.pricingByLocale = {
      'pt-BR': { currency: 'BRL', lowPrice: '497', highPrice: '1497' },
      'en-US': { currency: 'USD', lowPrice: '97', highPrice: '297' },
      'es-ES': { currency: 'EUR', lowPrice: '97', highPrice: '297' },
      'pt-PT': { currency: 'EUR', lowPrice: '97', highPrice: '297' },
      'en-GB': { currency: 'GBP', lowPrice: '79', highPrice: '249' }
    };

    this.softwareSchema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'SummDash',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web Browser',
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '97',
        highPrice: '297',
        priceCurrency: 'EUR',
        offerCount: '4',
        description: 'Free trial available'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        ratingCount: '523',
        bestRating: '5',
        worstRating: '1'
      }
    };
  }

  /**
   * Get current locale from URL or service
   */
  getCurrentLocale() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('lang') || localStorage.getItem('summdash-locale') || 'pt-BR';
  }

  /**
   * Update page title
   */
  setTitle(title) {
    document.title = title;
    this.updateMetaTag('og:title', title);
    this.updateMetaTag('twitter:title', title);
  }

  /**
   * Update meta description
   */
  setDescription(description) {
    this.updateMetaTag('description', description, 'name');
    this.updateMetaTag('og:description', description);
    this.updateMetaTag('twitter:description', description);
  }

  /**
   * Update or create meta tag
   */
  updateMetaTag(name, content, attribute = 'property') {
    let meta = document.querySelector(`meta[${attribute}="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attribute, name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  }

  /**
   * Set canonical URL
   */
  setCanonical(url) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
    this.updateMetaTag('og:url', url);
  }

  /**
   * Set Open Graph image
   */
  setImage(imageUrl, alt = '') {
    const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${BASE_URL}${imageUrl}`;
    this.updateMetaTag('og:image', fullUrl);
    this.updateMetaTag('og:image:alt', alt);
    this.updateMetaTag('twitter:image', fullUrl);
  }

  /**
   * Set Open Graph type
   */
  setType(type) {
    this.updateMetaTag('og:type', type);
  }

  /**
   * Generate hreflang alternates for all supported locales
   */
  generateHreflangAlternates(basePath) {
    const alternates = {};
    this.supportedLocales.forEach(locale => {
      alternates[locale] = `${BASE_URL}${basePath}${basePath.includes('?') ? '&' : '?'}lang=${locale}`;
    });
    return alternates;
  }

  /**
   * Set hreflang alternate links
   */
  setHreflangAlternates(alternates) {
    // Remove existing hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    // Add new ones
    Object.entries(alternates).forEach(([lang, url]) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', url);
      document.head.appendChild(link);
    });

    // Add x-default (use clean URL without lang param)
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    const firstUrl = Object.values(alternates)[0] || BASE_URL;
    xDefault.setAttribute('href', firstUrl.split('?')[0]);
    document.head.appendChild(xDefault);
  }

  /**
   * Add or update JSON-LD structured data
   */
  setStructuredData(data, id = 'structured-data') {
    let script = document.querySelector(`script#${id}`);
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }

  /**
   * Initialize base meta tags (call once on app init)
   */
  initBaseMeta() {
    const locale = this.getCurrentLocale();
    const ogLocale = locale.replace('-', '_');

    // Twitter Card type
    this.updateMetaTag('twitter:card', 'summary_large_image', 'name');
    this.updateMetaTag('twitter:site', '@summdash', 'name');

    // OG site name and locale
    this.updateMetaTag('og:site_name', 'SummDash');
    this.updateMetaTag('og:locale', ogLocale);

    // Add all alternate locales
    const alternateLocales = ['pt_BR', 'en_US', 'es_ES', 'pt_PT', 'en_GB'].filter(l => l !== ogLocale);
    alternateLocales.forEach(altLocale => {
      this.updateMetaTag('og:locale:alternate', altLocale);
    });
  }

  /**
   * Update SEO for Landing Page
   */
  updateLandingPage() {
    const locale = this.getCurrentLocale();
    const meta = this.defaultMeta[locale] || this.defaultMeta['pt-BR'];
    const pricing = this.pricingByLocale[locale] || this.pricingByLocale['en-US'];

    this.setTitle(meta.title);
    this.setDescription(meta.description);
    this.updateMetaTag('keywords', meta.keywords, 'name');
    this.setCanonical(BASE_URL);
    this.setType('website');
    this.setImage('/images/og-image.png', 'SummDash Dashboard Preview');

    // Hreflang for all 5 locales
    this.setHreflangAlternates({
      'pt-BR': `${BASE_URL}/?lang=pt-BR`,
      'en-US': `${BASE_URL}/?lang=en-US`,
      'es-ES': `${BASE_URL}/?lang=es-ES`,
      'pt-PT': `${BASE_URL}/?lang=pt-PT`,
      'en-GB': `${BASE_URL}/?lang=en-GB`
    });

    // Structured data with locale-specific pricing
    this.setStructuredData(this.organizationSchema, 'org-schema');

    // Software schema with locale-specific pricing
    const localizedSoftwareSchema = {
      ...this.softwareSchema,
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: pricing.lowPrice,
        highPrice: pricing.highPrice,
        priceCurrency: pricing.currency,
        offerCount: '4',
        description: 'Free trial available'
      }
    };
    this.setStructuredData(localizedSoftwareSchema, 'software-schema');
  }

  /**
   * Update SEO for Blog listing page
   */
  updateBlogPage(articles = []) {
    const locale = this.getCurrentLocale();

    const meta = {
      'pt-BR': {
        title: 'Blog SummDash - Estratégias de Marketing Digital para Agências',
        description: 'Artigos, guias e insights sobre Meta Ads, Google Ads, automação de relatórios e gestão de campanhas para agências de marketing digital.',
        keywords: 'blog marketing digital, meta ads, google ads, gestão de campanhas, agência marketing, relatórios automáticos, tráfego pago'
      },
      'en-US': {
        title: 'SummDash Blog - Digital Marketing Strategies for Agencies',
        description: 'Articles, guides and insights about Meta Ads, Google Ads, report automation and campaign management for digital marketing agencies.',
        keywords: 'digital marketing blog, meta ads, google ads, campaign management, marketing agency, automated reports, paid traffic'
      },
      'es-ES': {
        title: 'Blog SummDash - Estrategias de Marketing Digital para Agencias',
        description: 'Artículos, guías e insights sobre Meta Ads, Google Ads, automatización de informes y gestión de campañas para agencias de marketing digital.',
        keywords: 'blog marketing digital, meta ads, google ads, gestión de campañas, agencia marketing, informes automáticos, tráfico pagado'
      },
      'pt-PT': {
        title: 'Blog SummDash - Estratégias de Marketing Digital para Agências',
        description: 'Artigos, guias e insights sobre Meta Ads, Google Ads, automação de relatórios e gestão de campanhas para agências de marketing digital.',
        keywords: 'blog marketing digital, meta ads, google ads, gestão de campanhas, agência marketing, relatórios automáticos, tráfego pago'
      },
      'en-GB': {
        title: 'SummDash Blog - Digital Marketing Strategies for Agencies',
        description: 'Articles, guides and insights about Meta Ads, Google Ads, report automation and campaign management for digital marketing agencies.',
        keywords: 'digital marketing blog, meta ads, google ads, campaign management, marketing agency, automated reports, paid traffic'
      }
    };

    const localeMeta = meta[locale] || meta['pt-BR'];

    this.setTitle(localeMeta.title);
    this.setDescription(localeMeta.description);
    this.updateMetaTag('keywords', localeMeta.keywords, 'name');
    this.setCanonical(`${BASE_URL}/blog`);
    this.setType('website');
    this.setImage('/images/og-blog.png', 'SummDash Blog');

    // Hreflang for all 5 locales
    this.setHreflangAlternates({
      'pt-BR': `${BASE_URL}/blog?lang=pt-BR`,
      'en-US': `${BASE_URL}/blog?lang=en-US`,
      'es-ES': `${BASE_URL}/blog?lang=es-ES`,
      'pt-PT': `${BASE_URL}/blog?lang=pt-PT`,
      'en-GB': `${BASE_URL}/blog?lang=en-GB`
    });

    // Blog listing schema
    this.setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'SummDash Blog',
      description: localeMeta.description,
      url: `${BASE_URL}/blog`,
      inLanguage: locale,
      publisher: {
        '@type': 'Organization',
        name: 'SummDash',
        logo: {
          '@type': 'ImageObject',
          url: `${BASE_URL}/images/logo_black.png`
        }
      }
    }, 'blog-schema');

    // ItemList schema for article collection (helps Google understand blog structure)
    if (articles && articles.length > 0) {
      this.setStructuredData({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: articles.slice(0, 10).map((article, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `${BASE_URL}/blog/${article.slug}`,
          name: article.title
        }))
      }, 'itemlist-schema');
    }

    // CollectionPage schema
    this.setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: localeMeta.title,
      description: localeMeta.description,
      url: `${BASE_URL}/blog`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'SummDash',
        url: BASE_URL
      }
    }, 'collection-schema');
  }

  /**
   * Update SEO for Blog post page
   */
  updateBlogPost(article) {
    if (!article) return;

    const locale = this.getCurrentLocale();

    // Get localized content
    const title = locale === 'en-US' && article.titleEn ? article.titleEn :
                  locale === 'es-ES' && article.titleEs ? article.titleEs : article.title;

    const description = locale === 'en-US' && article.excerptEn ? article.excerptEn :
                        locale === 'es-ES' && article.excerptEs ? article.excerptEs : article.excerpt;

    const category = locale === 'en-US' && article.categoryEn ? article.categoryEn :
                     locale === 'es-ES' && article.categoryEs ? article.categoryEs : article.category;

    const imageUrl = article.image.startsWith('http') ? article.image : `${BASE_URL}${article.image}`;
    const articleUrl = `${BASE_URL}/blog/${article.slug}`;

    this.setTitle(`${title} | Blog SummDash`);
    this.setDescription(description);
    this.setCanonical(articleUrl);
    this.setType('article');
    this.setImage(article.image, title);

    // Article-specific Open Graph tags
    this.updateMetaTag('article:published_time', article.date);
    this.updateMetaTag('article:modified_time', article.date);
    this.updateMetaTag('article:author', 'SummDash');
    this.updateMetaTag('article:section', category);
    if (article.tags && article.tags.length > 0) {
      article.tags.forEach(tag => {
        this.updateMetaTag('article:tag', tag);
      });
    }

    // Keywords meta
    if (article.tags) {
      this.updateMetaTag('keywords', article.tags.join(', '), 'name');
    }
    if (article.seo && article.seo.keywords) {
      this.updateMetaTag('keywords', article.seo.keywords, 'name');
    }

    // Hreflang for all 5 locales
    this.setHreflangAlternates({
      'pt-BR': `${articleUrl}?lang=pt-BR`,
      'en-US': `${articleUrl}?lang=en-US`,
      'es-ES': `${articleUrl}?lang=es-ES`,
      'pt-PT': `${articleUrl}?lang=pt-PT`,
      'en-GB': `${articleUrl}?lang=en-GB`
    });

    // Calculate word count and reading time in ISO 8601 format
    const wordCount = article.content ? article.content.split(/\s+/).length : 1000;
    const readingTimeISO = `PT${article.readTime || Math.ceil(wordCount / 200)}M`;

    // Article schema (enhanced)
    this.setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${articleUrl}#article`,
      headline: title,
      alternativeHeadline: article.seo?.title || title,
      description: description,
      image: {
        '@type': 'ImageObject',
        url: imageUrl,
        width: 800,
        height: 450
      },
      author: {
        '@type': 'Organization',
        name: article.author || 'SummDash',
        url: BASE_URL
      },
      publisher: {
        '@type': 'Organization',
        name: 'SummDash',
        url: BASE_URL,
        logo: {
          '@type': 'ImageObject',
          url: `${BASE_URL}/images/logo_black.png`,
          width: 200,
          height: 60
        }
      },
      datePublished: article.date,
      dateModified: article.date,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': articleUrl
      },
      wordCount: wordCount,
      timeRequired: readingTimeISO,
      articleSection: category,
      inLanguage: locale,
      isAccessibleForFree: true,
      keywords: article.tags ? article.tags.join(', ') : '',
      copyrightYear: new Date(article.date).getFullYear(),
      copyrightHolder: {
        '@type': 'Organization',
        name: 'SummDash'
      }
    }, 'article-schema');

    // Breadcrumb schema
    this.setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: BASE_URL
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: `${BASE_URL}/blog`
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: category,
          item: `${BASE_URL}/blog?category=${encodeURIComponent(article.category)}`
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: title,
          item: articleUrl
        }
      ]
    }, 'breadcrumb-schema');

    // WebPage schema
    this.setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': articleUrl,
      url: articleUrl,
      name: title,
      description: description,
      isPartOf: {
        '@type': 'WebSite',
        name: 'SummDash',
        url: BASE_URL
      },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: imageUrl
      },
      breadcrumb: {
        '@id': `${articleUrl}#breadcrumb`
      }
    }, 'webpage-schema');
  }

  /**
   * Update SEO for generic page
   */
  updateGenericPage(pageMeta) {
    const locale = this.getCurrentLocale();
    const { title, description, path, image } = pageMeta;

    this.setTitle(title[locale] || title['pt-BR']);
    this.setDescription(description[locale] || description['pt-BR']);
    this.setCanonical(`${BASE_URL}${path}`);
    this.setType('website');

    if (image) {
      this.setImage(image);
    }

    // Hreflang for all 5 locales
    this.setHreflangAlternates({
      'pt-BR': `${BASE_URL}${path}?lang=pt-BR`,
      'en-US': `${BASE_URL}${path}?lang=en-US`,
      'es-ES': `${BASE_URL}${path}?lang=es-ES`,
      'pt-PT': `${BASE_URL}${path}?lang=pt-PT`,
      'en-GB': `${BASE_URL}${path}?lang=en-GB`
    });
  }

  /**
   * Remove a specific structured data script
   */
  removeStructuredData(id) {
    const script = document.querySelector(`script#${id}`);
    if (script) script.remove();
  }

  /**
   * Clean all dynamic SEO elements (useful when navigating away)
   */
  cleanup() {
    // Remove dynamic structured data
    ['article-schema', 'breadcrumb-schema', 'webpage-schema', 'itemlist-schema', 'collection-schema'].forEach(id => {
      this.removeStructuredData(id);
    });
  }
}

export const seoService = new SEOService();
